import React, { useState, useEffect, useCallback, PropsWithChildren } from 'react';
import { FavoriteTripsContext } from './useFavoriteTrips';
import { FavoriteTrip } from './types';

export const FavoriteTripsProvider: React.FC<PropsWithChildren<object>> = ({
  children,
}) => {
  const [favorites, setFavorites] = useState<FavoriteTrip[]>([]);

  useEffect(() => {
    try {
      const storedData = localStorage.getItem('fpData');
      if (storedData) {
        try {
          const data = JSON.parse(storedData);
          if (data.favorites) {
            setFavorites(data.favorites);
          }
        } catch (error) {
          setFavorites([]);
          localStorage.removeItem('fpData');
          console.error('Failed to parse favorites from localStorage:', error);
        }
      }
    } catch (error) {
      console.error('localStorage is not available:', error);
      setFavorites([]);
    }
  }, []);

  const saveFavoriteTrips = useCallback((favorites: FavoriteTrip[]) => {
    const existingData = JSON.parse(localStorage.getItem('fpData') || '{}');
    localStorage.setItem('fpData', JSON.stringify({ ...existingData, favorites }));
  }, []);

  const addFavoriteTrip = useCallback(
    (tripId: string) => {
      if (!favorites.some((favorite) => favorite.tripId === tripId)) {
        const updatedFavorites = [...favorites, { tripId }];
        setFavorites(updatedFavorites);
        saveFavoriteTrips(updatedFavorites);
        document.dispatchEvent(
          new CustomEvent('favoriteChanged', { detail: { tripId, action: 'add' } }),
        );
      }
    },
    [favorites, saveFavoriteTrips],
  );

  const removeFavoriteTrip = useCallback(
    (tripId: string) => {
      const updatedFavorites = favorites.filter((favorite) => favorite.tripId !== tripId);
      setFavorites(updatedFavorites);
      saveFavoriteTrips(updatedFavorites);
      document.dispatchEvent(
        new CustomEvent('favoriteChanged', { detail: { tripId, action: 'remove' } }),
      );
    },
    [favorites, saveFavoriteTrips],
  );

  const isFavoriteTrip = useCallback(
    (tripId: string) => {
      return favorites.some((favorite) => favorite.tripId === tripId);
    },
    [favorites],
  );

  return (
    <FavoriteTripsContext.Provider
      value={{ favorites, addFavoriteTrip, removeFavoriteTrip, isFavoriteTrip }}
    >
      {children}
    </FavoriteTripsContext.Provider>
  );
};
